import { FC } from 'react';

import { WithAvailable } from '../../../common/base';
import { CollectionConfig, CollectionItem, CollectionServices } from '../../base/collections';

/**
 * Defines the type of menu items in the "Others" area.
 */
export enum OthersMenuItemType {
  /**
   * Item type to display a divider between others menu item
   */
  DIVIDER = 'DIVIDER',
  /**
   * Item type to display a widget in the others menu
   */
  WIDGET = 'WIDGET',
  /**
   * Item type to display a React component in the others menu
   */
  COMPONENT = 'COMPONENT',
}

/**
 * Base configuration for menu items in the "Others" area.
 */
export interface OthersMenuBaseConfig extends CollectionConfig, WithAvailable {
  /**
   * The type of the others menu item. It acts as a TypeScript discriminator.
   */
  type: OthersMenuItemType;
}

/**
 * The configuration object of an other menu item that displays a widget.
 */
export interface OthersMenuWidgetConfig extends OthersMenuBaseConfig {
  /**
   * Type discriminator to indicate that config contains a widget configuration object.
   */
  type: OthersMenuItemType.WIDGET;
  /**
   * The name of the widget which need to be displayed.
   * A widget can be registered with the
   * {@link  "AppFrame Services Interface".Services.registerWidget | registerWidget}
   * service.
   */
  widget: string;
  /**
   * Extra CSS class to be added to the widget root.
   */
  className?: string;
}

/**
 * Configuration object of an other menu item that displays a divider.
 */
export interface OthersMenuDividerConfig extends OthersMenuBaseConfig {
  /**
   * Type discriminator to indicate that config contains a divider configuration object.
   */
  type: OthersMenuItemType.DIVIDER;
}

/**
 * Configuration object of an other menu item that displays a component.
 *
 * NOTE: component rendering is not the safest way to render components in the others menu. It is
 * mostly used in the AppFrame to render predefined components.
 *
 * It is recommended to use the widget rendering instead, which is more flexible and allows to use
 * the widget services to render the component in a microfrontend way.
 */
export interface OthersMenuComponentConfig extends OthersMenuBaseConfig {
  /**
   * Type discriminator to indicate that config contains a component configuration object.
   */
  type: OthersMenuItemType.COMPONENT;
  /**
   * The React component to render.
   */
  Component: FC;
  /**
   * Extra props to pass to the component.
   */
  ComponentProps?: Record<string, any>;
}

/**
 * OtherMenuItemConfig is a union type that represents all possible configurations for menu items
 * in the "Others" area.
 *
 * It includes configurations for different types of others menue items such as widgets, dividers
 * or components.
 */
export type OthersMenuItemConfig = OthersMenuWidgetConfig | OthersMenuDividerConfig | OthersMenuComponentConfig;

/**
 * The base interface for all "Others" menu items.
 *
 * It extends the {@link CollectionItem} interface to include the `id` and `order` properties
 * which are required for all "Others" menu items.
 */
export interface OthersMenuItemBase extends CollectionItem, OthersMenuBaseConfig {
  order: number;
  id: string;
}

/**
 * Collection services to manage the state and displayed items of mobile navigation "Others"
 * area.
 */
export type OthersServices = CollectionServices<OthersMenuBaseConfig, OthersMenuItemBase>;

/**
 * Defines the interface for a widget menu item in the "Others" area.
 *
 * It is the same as {@link OthersMenuWidgetConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface OthersMenuWidgetItem
  extends Omit<OthersMenuItemBase, 'type'>,
    Omit<OthersMenuWidgetConfig, 'id' | 'order'> {}

/**
 * Defines the interface for a divider menu item in the "Others" area.
 *
 * It is the same as {@link OthersMenuDividerConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface OthersMenuDividerItem
  extends Omit<OthersMenuItemBase, 'type'>,
    Omit<OthersMenuDividerConfig, 'id' | 'order'> {}

/**
 * Defines the interface for a component menu item in the "Others" area.
 *
 * It is the same as {@link OthersMenuComponentConfig} but it is always contains the `id` and `order`
 * properties which are assigned after the item configuration added by the `addItem` method.
 */
export interface OthersMenuComponentItem
  extends Omit<OthersMenuItemBase, 'type'>,
    Omit<OthersMenuComponentConfig, 'id' | 'order'> {}

/**
 * OthersMenuItem is a union type that represents all possible menu items in the "Others" area.
 *
 * They are very similar to {@link OthersMenuItemConfig} but they have both `id` and `order`
 * properties filled.
 */
export type OthersMenuItem = OthersMenuDividerItem | OthersMenuWidgetItem | OthersMenuComponentItem;
